// "Download (PDF)" of the coach Daily Lesson Plan, drawn like the admin's Create Schedule page
// (pages/AdminLessonPlan.jsx): the title, the Date / Balgruha / Coaches / time boxes, then for each
// visit "Lesson Plan — <Balgruha>" with its children count, the two-row plan table (Computer Apps,
// One-on-One — Personalized Learning, Special Instructions) and the Health Check, General
// Instructions and Attachments panels. Colours come from pages/AdminDailySchedule.css.
import React from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CalendarDays, Clock, FileText, Heart, House, Laptop, Paperclip, Target, Users } from 'lucide-react';
import { LEARNING_COLUMNS } from '../../pages/lessonPlanColumns';
import { COLUMN_ICONS, cellIcon, fixedAppMeta, rowLearning } from '../../pages/lessonPlanCells';
import { COMPUTER_APPS } from '../../constants/lessonPlanCatalog';

const C = {
    title: '#0b2fb0',
    subtitle: '#2357e6',
    navy: '#0b2a9e',
    royal: '#0a32e6',
    ink: '#0f172a',
    muted: '#5b6a8f',
    panelLine: '#d9e6f7',
    tableLine: '#dbe6f7',
    head: '#eef4fd',
    subHead: '#f4f8fe',
    even: '#fbfdff',
    box: '#d0dcf0',
    field: '#b9cbe8',
    tile: '#e3effc',
    pill: '#d3e0f5',
    pink: '#ec1f6f',
    pinkBg: '#fde4ec',
    orange: '#f59e0b',
    orangeBg: '#fff1d9',
    blue: '#1d4ed8',
};

// A4 landscape, in mm.
const PAGE_W = 297;
const PAGE_H = 210;
const M = 10;
const W = PAGE_W - 2 * M;
const ICON_GAP = 1.2;

// ---- Icons: the same lucide icons as the page, drawn to PNG once and reused. ----
const ICON_PX = 64;
const iconCache = new Map();
const iconKey = ({ Icon, color, fill }) => `${Icon.displayName || Icon.name}|${color}|${fill ? 'fill' : ''}`;

const svgOf = ({ Icon, color, fill }) => {
    const host = document.createElement('div');
    const root = createRoot(host);
    flushSync(() => root.render(React.createElement(Icon, { size: ICON_PX, color, fill: fill ? color : 'none' })));
    const markup = host.innerHTML;
    root.unmount();
    return markup;
};

const pngOf = (icon) => new Promise((resolve) => {
    let timer;
    const done = (value) => { clearTimeout(timer); resolve(value); };
    timer = setTimeout(() => done(null), 3000);
    try {
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = ICON_PX;
                canvas.height = ICON_PX;
                canvas.getContext('2d').drawImage(img, 0, 0, ICON_PX, ICON_PX);
                done(canvas.toDataURL('image/png'));
            } catch { done(null); }
        };
        img.onerror = () => done(null);
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgOf(icon))}`;
    } catch { done(null); }
});

// An icon that can't be drawn is left out; the PDF is still made.
const loadIcons = async (icons) => {
    const missing = new Map();
    icons.filter(Boolean).forEach((icon) => { const key = iconKey(icon); if (!iconCache.has(key)) missing.set(key, icon); });
    await Promise.all([...missing].map(async ([key, icon]) => { const png = await pngOf(icon); if (png) iconCache.set(key, png); }));
};

const drawIcon = (doc, icon, x, y, size) => {
    if (!icon) return;
    const key = iconKey(icon);
    const png = iconCache.get(key);
    if (png) doc.addImage(png, 'PNG', x, y, size, size, key);
};

const TITLE_ICON = { Icon: CalendarDays, color: C.title };
const FIELD_ICONS = {
    date: { Icon: CalendarDays, color: C.title },
    centre: { Icon: House, color: C.title, fill: true },
    coaches: { Icon: Users, color: C.title, fill: true },
    time: { Icon: Clock, color: C.title },
};
const HEAD_ICONS = { computer: { Icon: Laptop, color: C.blue }, note: { Icon: Target, color: C.blue } };
const PANEL_ICONS = {
    health: { Icon: Heart, color: C.pink, fill: true, bg: C.pinkBg },
    instructions: { Icon: FileText, color: C.orange, bg: C.orangeBg },
    attachments: { Icon: Paperclip, color: C.title, bg: C.head },
};

// ---- Page parts ----
const setText = (doc, size, style, color) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(color);
};

// Icon tile, "Daily Lesson Plan" and the page's subtitle.
const drawTitle = (doc) => {
    doc.setFillColor(C.tile);
    doc.roundedRect(M, 8, 12, 12, 2.4, 2.4, 'F');
    drawIcon(doc, TITLE_ICON, M + 2.6, 10.6, 6.8);
    setText(doc, 16, 'bold', C.title);
    doc.text('Daily Lesson Plan', M + 16, 13.4);
    setText(doc, 9, 'bold', C.subtitle);
    doc.text('Personalized lesson plan for each child', M + 16, 18.6);
    return 25;
};

// The row of labelled boxes (Date, Balgruha, Coaches, Start Time, End Time) in a bordered panel.
const drawFields = (doc, y, fields) => {
    const pad = 4;
    const gap = 4;
    const lineH = 3.9;
    const total = fields.reduce((sum, f) => sum + f.weight, 0);
    const inner = W - 2 * pad - gap * (fields.length - 1);
    setText(doc, 9, 'normal', C.ink);
    const laid = fields.map((f) => {
        const width = (inner * f.weight) / total;
        return { ...f, width, lines: doc.splitTextToSize(f.value || '—', width - 9.5) };
    });
    const boxH = Math.max(8.4, Math.max(...laid.map((f) => f.lines.length)) * lineH + 4.4);
    const panelH = 8 + boxH + 3.6;
    doc.setDrawColor(C.panelLine);
    doc.setLineWidth(0.3);
    doc.roundedRect(M, y, W, panelH, 2.4, 2.4, 'S');
    let x = M + pad;
    laid.forEach((f) => {
        setText(doc, 8.5, 'bold', C.navy);
        doc.text(f.label, x, y + 5.6);
        const by = y + 8;
        doc.setDrawColor(C.field);
        doc.setLineWidth(0.25);
        doc.roundedRect(x, by, f.width, boxH, 1.2, 1.2, 'S');
        drawIcon(doc, FIELD_ICONS[f.icon], x + 2, by + (boxH - 4) / 2, 4);
        setText(doc, 9, 'normal', f.value ? C.ink : C.muted);
        const top = by + boxH / 2 - ((f.lines.length - 1) * lineH) / 2;
        f.lines.forEach((line, i) => doc.text(line, x + 7.5, top + i * lineH, { baseline: 'middle' }));
        x += f.width + gap;
    });
    return y + panelH + 5;
};

// "Lesson Plan — <Balgruha>", the "N Children" pill and (All Balgruhas) the visit's time and coaches.
const drawCardHead = (doc, y, { name, count, meta }) => {
    const mid = y + 4;
    setText(doc, 13, 'bold', C.royal);
    const title = `Lesson Plan${name ? ` — ${name}` : ''}`;
    doc.text(title, M, mid, { baseline: 'middle' });
    const pill = `${count} ${count === 1 ? 'Child' : 'Children'}`;
    const px = M + doc.getTextWidth(title) + 4;
    setText(doc, 8.5, 'bold', C.navy);
    const pw = doc.getTextWidth(pill) + 7;
    doc.setFillColor(C.head);
    doc.setDrawColor(C.pill);
    doc.setLineWidth(0.25);
    doc.roundedRect(px, mid - 3, pw, 6, 3, 3, 'FD');
    doc.text(pill, px + pw / 2, mid, { baseline: 'middle', align: 'center' });
    if (meta) {
        setText(doc, 9, 'bold', C.navy);
        const tw = doc.getTextWidth(meta);
        doc.text(meta, M + W, mid, { baseline: 'middle', align: 'right' });
        drawIcon(doc, FIELD_ICONS.time, M + W - tw - 5.4, mid - 2, 4);
    }
    return y + 10;
};

// ---- The plan table ----
const COLUMN_WIDTHS = { number: 8, child: 30, computer: 34, note: 38 };
const learningWidth = (W - Object.values(COLUMN_WIDTHS).reduce((a, b) => a + b, 0)) / LEARNING_COLUMNS.length;
const HEAD_ICON = 3.4;
const BODY_ICON = 3.8;
const pad = (left, right, top = 2.2, bottom = 2.2) => ({ top, right, bottom, left });

const head = () => [
    [
        { content: '#', rowSpan: 2, styles: { halign: 'center', cellPadding: pad(1, 1) } },
        { content: 'Child Name', rowSpan: 2 },
        { content: 'Computer Apps', rowSpan: 2, icon: HEAD_ICONS.computer, styles: { cellPadding: pad(2.2 + HEAD_ICON + ICON_GAP, 2.2) } },
        { content: 'One-on-One — Personalized Learning', colSpan: LEARNING_COLUMNS.length, styles: { halign: 'center', textColor: C.royal, cellPadding: pad(2, 2, 1.6, 1.6) } },
        { content: 'Special Instructions', rowSpan: 2, icon: HEAD_ICONS.note, styles: { halign: 'center', cellPadding: pad(2.2 + HEAD_ICON + ICON_GAP, 2.2) } },
    ],
    LEARNING_COLUMNS.map(({ key, label }) => ({
        content: label,
        icon: COLUMN_ICONS[key],
        styles: { halign: 'center', fillColor: C.subHead, fontSize: 8, cellPadding: pad(1.2 + HEAD_ICON + ICON_GAP, 1.2, 1.8, 1.8) },
    })),
];

// A filled cell is a bordered box (with the column's icon before it); an empty one is a grey "—".
const valueCell = (value, icon, fillColor) => (value
    ? { content: value, box: true, icon: icon || undefined, styles: { fillColor, cellPadding: pad(icon ? 8 : 3, 2.6, 2.6, 2.6) } }
    : { content: '—', styles: { fillColor, textColor: C.muted } });

const body = (rows) => rows.map((row, i) => {
    const fillColor = i % 2 ? C.even : false;
    const learning = rowLearning(row);
    const computer = row.computer || COMPUTER_APPS[0].name;
    return [
        { content: String(i + 1), styles: { fillColor, halign: 'center', cellPadding: pad(1, 1) } },
        { content: row.childName || '', styles: { fillColor, fontStyle: 'bold', fontSize: 8.5, textColor: C.navy } },
        valueCell(computer, fixedAppMeta(computer), fillColor),
        ...LEARNING_COLUMNS.map(({ key }) => valueCell(learning[key], cellIcon(key, learning[key]), fillColor)),
        valueCell(row.note, null, fillColor),
    ];
});

const tableIcons = (rows) => [
    ...Object.values(HEAD_ICONS),
    ...Object.values(COLUMN_ICONS),
    ...rows.flatMap((row) => {
        const learning = rowLearning(row);
        return [fixedAppMeta(row.computer || COMPUTER_APPS[0].name), ...LEARNING_COLUMNS.map(({ key }) => cellIcon(key, learning[key]))];
    }),
];

const drawTable = (doc, startY, rows) => {
    autoTable(doc, {
        startY,
        margin: { top: 12, right: M, bottom: 12, left: M },
        theme: 'plain',
        head: head(),
        body: body(rows),
        rowPageBreak: 'avoid',
        tableLineColor: C.tableLine,
        tableLineWidth: 0.3,
        styles: {
            font: 'helvetica',
            fontSize: 8,
            textColor: C.ink,
            valign: 'middle',
            overflow: 'linebreak',
            cellPadding: pad(2.2, 2.2),
            // Column lines on every cell, like `.lp-table--plan tbody td` on the page.
            lineColor: C.tableLine,
            lineWidth: 0.25,
        },
        headStyles: { fillColor: C.head, textColor: C.navy, fontStyle: 'bold', fontSize: 8.5 },
        columnStyles: {
            0: { cellWidth: COLUMN_WIDTHS.number },
            1: { cellWidth: COLUMN_WIDTHS.child },
            2: { cellWidth: COLUMN_WIDTHS.computer },
            ...Object.fromEntries(LEARNING_COLUMNS.map((_, i) => [3 + i, { cellWidth: learningWidth }])),
            [3 + LEARNING_COLUMNS.length]: { cellWidth: COLUMN_WIDTHS.note },
        },
        didDrawCell: ({ cell, section }) => {
            const raw = cell.raw;
            if (!raw || typeof raw !== 'object') return;
            if (section === 'body' && raw.box) {
                const left = cell.x + (raw.icon ? 6.6 : 1.4);
                doc.setDrawColor(C.box);
                doc.setLineWidth(0.25);
                doc.roundedRect(left, cell.y + 1.2, cell.x + cell.width - 1.4 - left, cell.height - 2.4, 1, 1, 'S');
            }
            if (!raw.icon) return;
            if (section === 'body') {
                drawIcon(doc, raw.icon, cell.x + 1.6, cell.y + (cell.height - BODY_ICON) / 2, BODY_ICON);
                return;
            }
            // Header icons sit just before the (left-aligned or centred) label.
            const textX = cell.styles.halign === 'center'
                ? cell.getTextPos().x - Math.max(...cell.text.map((t) => doc.getTextWidth(t))) / 2
                : cell.x + cell.padding('left');
            drawIcon(doc, raw.icon, textX - ICON_GAP - HEAD_ICON, cell.y + (cell.height - HEAD_ICON) / 2, HEAD_ICON);
        },
    });
    return (doc.lastAutoTable?.finalY || startY) + 5;
};

// ---- Health Check / General Instructions / Attachments, side by side ----
const drawPanels = (doc, y, visit) => {
    const panels = [
        visit.healthCheck && { title: 'Health Check', icon: PANEL_ICONS.health, text: visit.healthCheck },
        visit.instructions && { title: 'General Instructions', icon: PANEL_ICONS.instructions, text: visit.instructions },
        visit.attachments?.length && { title: 'Attachments', icon: PANEL_ICONS.attachments, text: visit.attachments.join('\n') },
    ].filter(Boolean);
    if (!panels.length) return y;
    const gap = 4;
    const lineH = 4;
    const width = (W - gap * (panels.length - 1)) / panels.length;
    setText(doc, 8.5, 'normal', C.ink);
    const laid = panels.map((p) => ({ ...p, lines: doc.splitTextToSize(p.text, width - 8) }));
    const height = 15 + Math.max(...laid.map((p) => p.lines.length)) * lineH;
    if (y + height > PAGE_H - 12) { doc.addPage(); y = 12; }
    laid.forEach((p, i) => {
        const x = M + i * (width + gap);
        doc.setFillColor(C.even);
        doc.setDrawColor(C.tableLine);
        doc.setLineWidth(0.25);
        doc.roundedRect(x, y, width, height, 2, 2, 'FD');
        doc.setFillColor(p.icon.bg);
        doc.roundedRect(x + 4, y + 3, 7.6, 7, 1.4, 1.4, 'F');
        drawIcon(doc, p.icon, x + 5.8, y + 4.5, 4);
        setText(doc, 9.5, 'bold', C.title);
        doc.text(p.title, x + 14.5, y + 6.5, { baseline: 'middle' });
        setText(doc, 8.5, 'normal', p.title === 'Attachments' ? C.blue : C.ink);
        p.lines.forEach((line, n) => doc.text(line, x + 4, y + 14 + n * lineH));
    });
    return y + height + 5;
};

/**
 * Builds and saves the PDF.
 * visits: [{ name, start, end, coaches, rows, healthCheck, instructions, attachments: [filename] }]
 * One Balgruha: its time goes in the boxes at the top, as on the admin page. All Balgruhas: the top
 * boxes show the date, "All Balgruhas" and the coaches, and each visit's heading carries its time.
 */
export async function downloadLessonPlanPdf({ fileName, date, centreName, coachNames, allCentres, visits }) {
    await loadIcons([
        TITLE_ICON,
        ...Object.values(FIELD_ICONS),
        ...Object.values(PANEL_ICONS),
        ...visits.flatMap((v) => tableIcons(v.rows)),
    ]);

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    let y = drawTitle(doc);
    const only = allCentres ? null : visits[0];
    y = drawFields(doc, y, [
        { label: 'Date', value: date, icon: 'date', weight: 1.1 },
        { label: 'Balgruha', value: centreName, icon: 'centre', weight: 1.1 },
        { label: 'Coaches', value: coachNames, icon: 'coaches', weight: 1.45 },
        ...(only ? [
            { label: 'Start Time', value: only.start, icon: 'time', weight: 0.8 },
            { label: 'End Time', value: only.end, icon: 'time', weight: 0.8 },
        ] : []),
    ]);

    visits.forEach((visit, i) => {
        // Keep a visit's heading with its table header and first row.
        if (i > 0 && y + 40 > PAGE_H - 12) { doc.addPage(); y = 12; }
        const meta = allCentres ? [visit.start && visit.end ? `${visit.start} – ${visit.end}` : '', visit.coaches && `Coach: ${visit.coaches}`].filter(Boolean).join('   |   ') : '';
        y = drawCardHead(doc, y, { name: visit.name, count: visit.rows.length, meta });
        y = drawTable(doc, y, visit.rows);
        y = drawPanels(doc, y, visit) + 3;
    });

    doc.save(fileName);
}
